import React, { useState } from 'react';
import './Forms.css';

function AddTeacherForm({ onClose, onSuccess, teacher = null }) {
    const isEdit = !!teacher;

    const [formData, setFormData] = useState({
        fullName:      teacher?.fullName      ?? '',
        email:         teacher?.email         ?? '',
        phone:         teacher?.phone         ?? '',
        address:       teacher?.address       ?? '',
        qualification: teacher?.qualification ?? '',
        experience:    teacher?.experience?.toString() ?? '',
        joiningDate:   teacher?.joiningDate   ?? '',
        // In edit mode the password field starts blank — empty means "leave unchanged".
        // The stored value can't be pre-filled because passwords are bcrypt-hashed.
        password:      isEdit ? '' : 'password123',
        status:        teacher?.status        ?? 'ACTIVE'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const API_URL = window.location.hostname === 'localhost'
                ? 'http://localhost:5001/api'
                : 'https://welittleleaf.com/api';

            const url = isEdit
                ? `${API_URL}/admin/teachers/${encodeURIComponent(teacher.teacherId)}`
                : `${API_URL}/admin/teachers`;

            // On edit we only send the password if admin actually typed a new one.
            // Empty field = leave the existing password alone (server treats it that way).
            const body = isEdit && !formData.password
                ? (() => { const { password, ...rest } = formData; return rest; })()
                : formData;

            const response = await fetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.success) {
                onSuccess();
                onClose();
            } else {
                setError(data.message || (isEdit ? 'Failed to update teacher' : 'Failed to create teacher'));
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
            console.error(isEdit ? 'Error updating teacher:' : 'Error creating teacher:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEdit ? 'Edit Teacher' : 'Add New Teacher'}</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="form">
                    {error && <div className="error-message">{error}</div>}

                    <div className="form-group">
                        <label>Full Name *</label>
                        <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="Teacher's full name"
                            required
                        />
                        <small style={{color: '#6b7280', fontSize: '0.85rem'}}>
                            {isEdit ? `Employee ID: ${teacher.employeeId}` : 'Employee ID will be generated automatically'}
                        </small>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Email *</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="teacher@littleleaf.com"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Phone *</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+91-9876543210"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Address *</label>
                        <textarea
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="Complete address"
                            rows="3"
                            required
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Qualification *</label>
                            <input
                                type="text"
                                name="qualification"
                                value={formData.qualification}
                                onChange={handleChange}
                                placeholder="e.g., B.Ed, M.Ed"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Experience (years) *</label>
                            <input
                                type="number"
                                name="experience"
                                value={formData.experience}
                                onChange={handleChange}
                                placeholder="Years of experience"
                                min="0"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Joining Date *</label>
                            <input
                                type="date"
                                name="joiningDate"
                                value={formData.joiningDate}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>{isEdit ? 'New Password' : 'Password *'}</label>
                            <input
                                type="text"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder={isEdit ? 'Leave blank to keep current' : 'Login password'}
                                required={!isEdit}
                                autoComplete="new-password"
                            />
                            {isEdit && (
                                <small style={{color: '#6b7280', fontSize: '0.85rem'}}>
                                    Passwords are stored hashed and can't be displayed. Type a new one to rotate it; leave blank to keep the existing one.
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                        >
                            <option value="ACTIVE">Active</option>
                            <option value="INACTIVE">Inactive</option>
                        </select>
                    </div>

                    <div className="form-actions">
                        <button type="button" onClick={onClose} className="btn btn-secondary">
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? (isEdit ? 'Saving…' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Teacher')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddTeacherForm;
